import React, { FC, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
import Badge from '@mui/material/Badge';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import Divider from '@mui/material/Divider';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { ListNotificationsUseCase } from '../../Usecase/Notifications/list-notifications-usecase';
import { INotificationEntity, NotificationTypeEnum, TNotificationPriorityType } from '../../Services/api/notifications/notifications.interface';
import { useToast } from '../../Hooks/ToasContext';
import { useNotifications } from './use-notifications.hook';
import { StockDiscrepancyModal } from '../Modals/variants/stock-discrepancy/stock-discrepancy-modal.component';
import { MarkAsReadNotificationsUseCase } from '../../Usecase/Notifications/mark-as-read-notifications-usecase';

/**
 * Retorna o ícone apropriado baseado na prioridade da notificação
 * @param priority - Nível de prioridade da notificação (critical, high, medium, low)
 * @returns Elemento React com o ícone correspondente
 */
const getPriorityIcon = (priority: TNotificationPriorityType) => {
  const iconProps = { fontSize: 'small' as const };
  
  switch (priority) {
    case 'critical':
      return <ErrorOutlineIcon {...iconProps} color="error" />;
    case 'high':
      return <WarningAmberIcon {...iconProps} sx={{ color: '#ff9800' }} />;
    case 'medium':
      return <InfoOutlinedIcon {...iconProps} color="info" />;
    case 'low':
      return <FiberManualRecordIcon {...iconProps} sx={{ color: '#9e9e9e' }} />;
    default:
      return <InfoOutlinedIcon {...iconProps} color="primary" />;
  }
};

interface INotificationMenuProps {
  listNotificationsUseCase: ListNotificationsUseCase
  markAsReadNotificationUseCase: MarkAsReadNotificationsUseCase
}
const NotificationMenu: FC<INotificationMenuProps> = ({ listNotificationsUseCase, markAsReadNotificationUseCase }) => {
   
  const { anchorEl, open, handleClick, handleClose, loading, data, totalRegisters, page, loadNotifications, totalUnreadNotifications } = useNotifications({ listNotificationsUseCase })

  const [stockModalOpen, setStockModalOpen] = useState(false);
  const [selectedStockNotification, setSelectedStockNotification] = useState<INotificationEntity | null>(null);

  const handleNotificationClick = (notification: INotificationEntity) => {
    if (notification.type === NotificationTypeEnum.STOCK_PROBLEM) {
      setSelectedStockNotification(notification);
      setStockModalOpen(true);
    }
    handleClose();
  };

  const handleStockModalClose = () => {
    setStockModalOpen(false);
    setSelectedStockNotification(null);
  };

  return (
    <React.Fragment>
      <Box sx={{ display: 'flex', alignItems: 'center', textAlign: 'center' }}>
        <Tooltip title="Notificações">
          <IconButton
            onClick={handleClick}
            size="large"
            aria-controls={open ? 'notification-menu' : undefined}
            aria-haspopup="true"
            aria-expanded={open ? 'true' : undefined}
            color="primary"
            sx={totalUnreadNotifications > 0 ? {
              animation: 'bellPulse 1.5s ease-in-out infinite',
              '@keyframes bellPulse': {
                '0%': {
                  transform: 'scale(1)',
                },
                '50%': {
                  transform: 'scale(1.15)',
                },
                '100%': {
                  transform: 'scale(1)',
                },
              },
            } : {}}
          >
            <Badge 
              badgeContent={totalUnreadNotifications} 
              color="error"
              sx={{
                position: 'relative',
                '& .MuiBadge-badge': totalUnreadNotifications > 0 ? {
                  animation: 'badgePulse 1.5s ease-in-out infinite',
                  zIndex: 1,
                  '@keyframes badgePulse': {
                    '0%': {
                      boxShadow: '0 0 0 0 rgba(244, 67, 54, 1), 0 0 8px rgba(244, 67, 54, 0.8)',
                    },
                    '50%': {
                      boxShadow: '0 0 0 12px rgba(244, 67, 54, 0), 0 0 15px rgba(244, 67, 54, 0)',
                    },
                    '100%': {
                      boxShadow: '0 0 0 0 rgba(244, 67, 54, 0), 0 0 8px rgba(244, 67, 54, 0)',
                    },
                  },
                } : {},
              }}
            >
              <NotificationsOutlinedIcon color="primary" sx={{ position: 'relative', zIndex: 2 }} />
            </Badge>
          </IconButton>
        </Tooltip>
      </Box>
      <Menu
        anchorEl={anchorEl}
        id="notification-menu"
        open={open}
        onClose={handleClose}
        onClick={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: 'auto',
              filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              mt: 1.5,
              width: 400,
              maxHeight: '80vh',
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {data?.map((notification, index) => [
          <MenuItem 
            key={notification.id}
            onClick={() => handleNotificationClick(notification)} 
            sx={{ 
              py: 1.5, 
              alignItems: 'flex-start',
              cursor: notification.type === NotificationTypeEnum.STOCK_PROBLEM ? 'pointer' : 'default',
              '&:hover': {
                backgroundColor: notification.type === NotificationTypeEnum.STOCK_PROBLEM ? 'action.hover' : 'transparent'
              }
            }}
          >
            <Box sx={{ display: 'flex', gap: 1, width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', pt: 0.5 }}>
                {getPriorityIcon(notification.priority)}
              </Box>
              <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', flex: 1 }}>
                <Typography 
                  variant="subtitle2" 
                  sx={{ 
                    fontWeight: 'bold',
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    mb: 0.5
                  }}
                >
                  {notification.title}
                </Typography>
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  sx={{
                    wordBreak: 'break-word',
                    whiteSpace: 'normal',
                    fontSize: '0.875rem',
                    mb: 0.5,
                    lineHeight: '1.4em'
                  }}
                >
                  {notification.message}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
                  {notification.createdAt && new Date(notification.createdAt).toLocaleString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </Typography>
              </Box>
            </Box>
          </MenuItem>,
          index < (data.length - 1) && <Divider key={`divider-${notification.id}`} />
        ])}
      </Menu>

      {selectedStockNotification && (
        <StockDiscrepancyModal
          open={stockModalOpen}
          onClose={handleStockModalClose}
          title={selectedStockNotification.title}
          data={selectedStockNotification.metadata?.payload || []}
          finishedGoodsDiscrepancies={selectedStockNotification.metadata?.finishedGoodsDiscrepancies}
          allocatedDiscrepancies={selectedStockNotification.metadata?.allocatedDiscrepancies}
          workInProgressDiscrepancies={selectedStockNotification.metadata?.workInProgressDiscrepancies}
          notificationId={selectedStockNotification.id}
          markAsReadNotificationUseCase={markAsReadNotificationUseCase}
        />
      )}
    </React.Fragment>
  );
};

export { NotificationMenu }; 